<template>
  <div class="gmf-item-rewards">
    <div class="gmf-p-4">
      <div class="gmf-subheader">
        {{ titleOverride }}
      </div>
      <div class="gmf-mb-3">
        {{ descriptionOverride }}
      </div>  
    </div>
    <div class="gmf-item-rewards gmf-fullbody-reward" v-if="currentStep === 'avatar-3d-step'">
      <div class="gmf-item-rewards-fullbody">
        <FullBodyAvatar :customer="state.profile" />
        <button class="gmf-btn gmf-btn-primary" @click="openAvatarConfigurator">{{ t("profile_tabs.change_avatar") }}</button>
      </div>
    </div>
    <div class="gmf-item-stack" v-else>
      <ItemStack v-model="selectedRewardIndex" :items="mergedItems.filter(i => typeof i !== 'string')" ref="stack" />
    </div>
    <div class="gmf-item-rewards-actions gmf-mt-4">
      <button class="gmf-btn gmf-btn-primary" @click="nextSlide">
        {{ ! hasNextStep ? tr('close') : tr('next') }}
      </button>
    </div>
  </div>
</template>
<style>
.gmf-item-rewards {
  user-select: none;
  background-color: var(--gmf-col-lightgray);
}

.gmf-item-rewards-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gmf-p-3);
  padding: var(--gmf-p-4);
  padding-top: 0;
}

.gmf-item-rewards .gmf-header {
  width: 90%;
}

.gmf-fullbody-reward {
  width: 100%;
}

.gmf-item-rewards-fullbody {
  position: relative;
  overflow: hidden;  
}

.gmf-item-rewards-fullbody .gmf-btn {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  width: auto;
}

.gmf-item-rewards .gmf-fullbody-avatar {
  max-width: 1024px;
  max-height: 256px;
  margin-bottom: 30px;
}
.gmf-item-rewards .gmf-fullbody-avatar-content {
  height: 110%;
  left: -30px;
  width: unset;
}
.gmf-item-rewards .gmf-fullbody-avatar-customizable {
  top: unset;
  left: unset;
  bottom: 10px;
  right: 10px;
  transform: unset;
}
</style>
<script>
import bus from '@/utils/eventbus';
import ItemStack from './ItemStack.vue';
import FullBodyAvatar from '../FullBodyAvatar.vue';
import openAvatarConfigurator from '@/components/AvatarConfigurator/openAvatarConfigurator';
import confetti from '@/utils/confetti';

import state from '@/state';
const tr = state.t.prefix('item_rewards');

export default {
  components: {
    ItemStack,
    FullBodyAvatar
  },
  props: {
     items: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    showConfetti: {
      type: Boolean,
      default: true,
    },
    show3dAvatarStep: {
      type: Boolean,
      default: false,
    }
  },
  data () {
    return {
      state,
      selectedRewardIndex: 0
    };
  },
  mounted () {
    if (this.showConfetti) {
      const container = this.$el;
      const rect = container.getBoundingClientRect();
      
      // Center of container.
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
  
      confetti({
        particleCount: 100,
        x,
        y,
        zIndex: 1000000000,
        spread: 80,
        startVelocity: 45 * 70,
        scalar: 1.5,
        ttl: 1.2,
      });
    }
  },
  computed: {
    use3DAvatar () {
      return state.merchantConfig.avatars.enabled === '3D';
    },
    /**
     * Merges items with the same uuid (e.g. multiple colors of the same item).
     */
    mergedItems () {
      const mergedItemsMap = {};

      for (const item of this.items) {
        let key = item.uuid || item.name;
        if (!this.use3DAvatar) {
          key = item.url;
        }
        if (! mergedItemsMap[key]) {
          mergedItemsMap[key] = {
            ...item,
            variations: {
              colors: [],
            }
          };
        } else {
          if (item.metadata?.quantity) {
            mergedItemsMap[key].metadata.quantity += item.metadata?.quantity || 0;
          }
        }
        if (mergedItemsMap[key].type === 'token') {
          let rarity = 'common';
          if (mergedItemsMap[key].metadata?.quantity >= 1000) {
            rarity = 'rare';
          } else if (mergedItemsMap[key].metadata?.quantity >= 250) {
            rarity = 'uncommon';
          }
          mergedItemsMap[key].rarity = rarity;
        }

        if (item.variation?.color) {
          mergedItemsMap[key].variations.colors.push(item.variation.color);
        }
      }

      const mergedItems = Object.values(mergedItemsMap);
      if (this.show3dAvatarStep && this.hasAvatarPart) {
        mergedItems.push('avatar-3d-step');
      }

      return mergedItems;
    },    
    hasAvatarPart () {
      return this.items?.some((item) => item.type == "avatar-3d-part");
    },
    currentStep () {
      return this.mergedItems[this.selectedRewardIndex];
    },
    hasNextStep () {
      return this.selectedRewardIndex < this.mergedItems.length - 1
    },
    titleOverride () {
      if (this.currentStep === 'avatar-3d-step') {
        return tr('avatar_slide_title');
      }
      return this.title;
    },
    descriptionOverride () {
      if (this.currentStep === 'avatar-3d-step') {
        return tr('avatar_slide_description');
      }
      return this.description;
    }
  },
  methods: {
    tr,
    t: state.t,
    nextSlide () {
      if (! this.hasNextStep) {
        this.close();
      } else if (this.mergedItems[this.selectedRewardIndex+1] === 'avatar-3d-step') {
        this.selectedRewardIndex++;
      } else {
        this.$refs.stack.next();
      }
    },
    close () {
      this.$emit('close');
    },
    openAvatarConfigurator () {
      bus.emit('modal.close');
      openAvatarConfigurator();
    },
  },
};
</script>
